'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { ChangeEvent, useEffect, useState } from 'react'

import Icon from '@/components/ui/icon'
import { cleanText, cn } from '@/lib/utils'
import { CategoryProperty, SupplierFilter } from '@/types/category.type'
import { useTranslations } from 'next-intl'
import RadioCustom from '@/components/ui/Radio'

type FilterItemMobileProps = {
  type: 'brand' | 'prid' | 'stock'
  suppliers: SupplierFilter[] | null
  categoryProperty: CategoryProperty | null
  isPridDisable?: (id: number) => boolean
  onClose: () => void
  hasSelected: boolean
  disableGlobal?: boolean
  prids?: number[]
  setPrids?: (selected: number[]) => void
  totalInStock: number
  total: number
}

type Formated = {
  id: number
  name: string
  count: number
}

export default function FilterItemMobile({
  categoryProperty,
  suppliers,
  type,
  hasSelected,
  disableGlobal = false,
  isPridDisable,
  setPrids,
  totalInStock,
  total,
  prids,
  
}: FilterItemMobileProps) {
  const [data, setData] = useState<Formated[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [filteredData, setFilteredData] = useState<Formated[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [filterVal, setFilterVal] = useState('')
  const t = useTranslations('product_detail')
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const selectedPridsCount = prids && categoryProperty?.childrens.filter((c) => prids.includes(c.id)).length || 0
  const isSelected = (id: number) => selected.includes(id)

  const disablePrid = (id: number) => {
    return typeof isPridDisable === 'function' ? isPridDisable(id) : false
  }

  const handleSelected = (id: number) => {
    if (disablePrid(id)) return

    const params = new URLSearchParams(searchParams)
    params.delete('page')

    if (type === 'stock') {
      if (selected.includes(id)) {
        setSelected([])
        params.delete('in_stock')
      } else {
        setSelected([id])
        params.set('in_stock', id.toString())
      }
    } else {
      if (type === 'brand') {
        const nextSelected = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]

        setSelected(nextSelected)

        if (nextSelected.length > 0) {
          params.set('brand', [...new Set(nextSelected)].join(','))
        } else {
          params.delete('brand')
        }
      } else {
        const prid = (searchParams.get('prid') ?? '').split(',').filter(Boolean).map(Number)
        const pridLocal = categoryProperty?.childrens.map((c) => c.id) ?? []

        const nextSelected = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]
        const finalPrid = getFinalPrid(prid, pridLocal, nextSelected).join(',')

        setSelected(nextSelected)

        if (finalPrid) {
          params.set('prid', finalPrid)
        } else {
          params.delete('prid')
        }

        if (typeof setPrids === 'function') setPrids(nextSelected)
      }
    }

    router.push(`${pathname}?${params.toString().replace(/%2C/g, ',')}`)
  }

  const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFilterVal(value)

    if (value === '') {
      setFilteredData(data) // Nếu input rỗng, hiển thị lại toàn bộ data gốc
    } else {
      setFilteredData(data.filter((d) => cleanText(d.name).toLowerCase().includes(cleanText(value).toLowerCase())))
    }
  }

  const getFinalPrid = (arr1: number[], arr2: number[], arr3: number[]) => {
    // Bước 1: Xóa tất cả ID của thuộc tính khỏi arr1
    const updatedArr1 = arr1.filter((id) => !arr2.includes(id))

    // Bước 2: Thêm các ID từ arr3 vào arr1
    updatedArr1.push(...arr3)

    return updatedArr1
  }

  const titleName = () => {
    if (type === 'brand') {
      return t('supplier')
    } else if (type === 'prid') {
      return categoryProperty?.name
    } else {
      return t('stock')
    }
  }

  useEffect(() => {
    let newData: Formated[] = []

    if (suppliers) {
      newData = [...newData, ...suppliers.map((s) => ({ id: s.id, name: s.name, count: Number(s.products_count) }))]
    }

    if (categoryProperty?.childrens) {
      newData = [...newData, ...categoryProperty.childrens.map((c) => ({ id: c.id, name: c.name, count: c.actual_count > 0 ? c.actual_count : c.products_count }))]
    }

    if (!suppliers && !categoryProperty?.childrens) {
      newData = [...newData, { id: 0, name: t('all'), count: Number(total) }, { id: 1, name: t('has_stock'), count: Number(totalInStock) }]
    }

    if (newData.length > 0) {
      setData(newData)
      setFilteredData(newData)
    }
  }, [suppliers, categoryProperty?.childrens, total, totalInStock, t])

  useEffect(() => {
    if (type === 'brand') {
      const arr1 = (searchParams.get('brand') ?? '').split(',').filter(Boolean).map(Number)
      const arr2 = suppliers?.map((s) => s.id) ?? []
      const set2 = new Set(arr2)
      const commonNumbers = arr1.filter((num) => set2.has(num))
      setSelected(commonNumbers)
    } else if (type === 'prid') {
      const arr1 = (searchParams.get('prid') ?? '').split(',').filter(Boolean).map(Number)
      const arr2 = categoryProperty?.childrens.map((c) => c.id) ?? []
      const set2 = new Set(arr2)
      const commonNumbers = arr1.filter((num) => set2.has(num))
      setSelected(commonNumbers)
    } else {
      const inStock = searchParams.get('in_stock')

      setSelected(inStock ? [Number(inStock)] : [])

    }
  }, [categoryProperty?.childrens, searchParams, suppliers, type])

  return (
    <li className="mb-[15px]">
      <div
        className={cn('flex items-center bg-white p-[10px]', {
          'opacity-40': disableGlobal,
        })}
        onClick={() => {
          if (disableGlobal) return
          setIsOpen(!isOpen)
        }}
      >
        <div className="flex-1">
          <b className={cn({ 'text-green-700': hasSelected })}>{titleName()} {selectedPridsCount > 0 ? `(${selectedPridsCount})` : ''}</b>
        </div>
      </div>
      {isOpen && (
        <div className="bg-white border-t border-bordercolor p-2">
          {
            type !== 'stock' ? (
              <div className="mb-4 relative">
                <input
                  type="text"
                  value={filterVal}
                  onChange={handleFilter}
                  className="outline-none border border-bordercolor w-full rounded pl-2 pr-7 py-1"
                  placeholder={type === 'brand' ? 'Tìm thương hiệu' : `Tìm ${categoryProperty?.name}`}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-2 bg-white">
                  <Icon icon="tgic icon-search" />
                </div>
              </div>
            ) : null
          }

          <div className="max-h-[350px] overflow-y-auto scrollbar-thin">
            <ul>
              {filteredData.map((d) => (
                <li
                  key={`filter-item-${d.id}`}
                  className={cn('group flex items-center gap-2 p-1.5 mb-2 text-sm border border-transparent cursor-pointer hover:text-primary', {
                    // 'bg-[#e2fde2] text-green-700 hover:bg-[#e2fde2] border-l-green-700 hover:border-l-gray-700': isSelected(d.id),
                    'opacity-40 hidden cursor-default hover:border-l-transparent hover:text-current': disablePrid(d.id),
                  })}
                  onClick={() => handleSelected(d.id)}
                >
                  <RadioCustom
                    shape="square"
                    checked={isSelected(d.id)}
                    id={d.id.toString()}
                    disabled={disablePrid(d.id)}
                    className=""
                  />
                  <div>
                    {d.name} {d.count > 0 && <span className="text-primary">({d.count})</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </li>
  )
}
